import cloneDeep from 'lodash/cloneDeep'
import { watch } from 'vue'
import throttle from 'lodash/throttle'
import Geographic from '@/lib/geographic'
import DrawModeBase from '@/lib/mapbox/draw-mode-base'
import {
  getRouteGeoJson,
  getPreviewGeoJson,
  getWaypointMarkerGeoJson,
  showRouteWaypointPopup,
  removeRouteWaypointPopup,
} from '@/lib/mapbox/route-mode-helpers'
import routeService from '@/services/route-service'
import { BUILD_MODE, EDIT_MODE } from '@/services/route-service-constants'
import {
  setFeaturesHighlighted,
  clearFeaturesHighlighted,
} from '@/services/map-service'
import { requestElevation } from '@/services/elevation-service'
import { getItem } from '@/plugins/storage'

import { lineString, point } from '@turf/turf'
import nearestPointOnLine from '@turf/nearest-point-on-line'
import {
  DEFAULT_UNIT_TYPE,
  metersToFeet,
  metersToMiles,
  metersToKilometers,
  kilometersToMeters,
} from '@/components/shared/x-distance.vue'
import { commaSeparatedNumber } from '@/lib/math'
import { coordsOnLine } from '@/components/elevationProfile/elevation-profile-container.vue'
import markupService from '@/services/markup-service'

/**
 * Default state for the mode.
 */
const DEFAULT_STATE = Object.freeze({
  prevClickLocation: null,
  draggedMarkerIndex: null,
  didDragMarker: false,
  undraggedRouteDistance: null,
  preventMapClick: false,
  routeHoverLocation: null,
  markerHoverLocation: null,
  selectedWaypointLocation: null,
  sampledLineCoordinates: null,
  routeElevation: null,
  isImperial: true,
  dragLocation: null,
})

/**
 * Throttle interval for noisy events (e.g., onMouseMove)
 */
const THROTTLE_INTERVAL = 60

/**
 * Translates map interactions into commands to build a route.
 */
class RouteCreateMode extends DrawModeBase {
  /**
   * @inheritDoc
   */
  onSetup() {
    const state = cloneDeep(DEFAULT_STATE)
    this._state = state

    // Render features on:
    // - changes to the waypoints or route (which will update waypoints).
    watch(
      () => routeService.waypoints,
      (waypoints) => this._handleWaypointsUpdated(waypoints),
      // Array elements are mutated in place, so ensure they're watched as well.
      { deep: true }
    )
    // - changes to the current preview.
    watch(
      () => routeService.previewSegment,
      () => this.updated()
    )

    // - changes to the current route geoJson
    watch(
      () => routeService?.route?.geoJson?.coordinates,
      (coordinates) => this._handleRouteLineUpdated(coordinates),
      { deep: true, immediate: true }
    )

    // - changes to the focused route waypoint index
    watch(
      () => routeService.focusedWaypointIndex,
      (value) => {
        if (value === null) {
          this._removeSelectedWaypoint()
        }
      }
    )

    // changes to user unit preferences
    watch(
      () => getItem('unitType'),
      (value) => {
        this._state.isImperial = value === DEFAULT_UNIT_TYPE
      },
      { immediate: true }
    )

    this._mouseMove = throttle((e) => {
      this._handleMouseMove(e)
    }, THROTTLE_INTERVAL)
  }

  /**
   * @inheritDoc
   */
  // eslint-disable-next-line require-await
  async toDisplayFeatures(routeGeoJson) {
    const features = [routeGeoJson]
    const { color, style, weight } = routeGeoJson.properties
    const isDragPreview = this._state.didDragMarker

    const route = getRouteGeoJson()
    if (route) {
      routeGeoJson.geometry = route
    } else {
      routeGeoJson.geometry = null
    }

    const preview = getPreviewGeoJson(color, style, weight, isDragPreview)
    if (preview) {
      features.push(preview)

      if (this._isDraggingMarker) {
        const dragLabel = this._formatDragLabel()
        features.push(dragLabel)
      }
    }

    const markers = getWaypointMarkerGeoJson(isDragPreview, color)
    features.push(...markers)

    if (this._isHoveringRoute()) {
      const hoverPoint = this._formatHoverPoint()
      features.push(hoverPoint)

      const routeHoverLabel = this._formatHoverLabel(
        'Click to add point,\n Drag to change route\n'
      )
      features.push(routeHoverLabel)
    }

    if (this._isHoveringMarker()) {
      const markerHoverLabel = this._formatHoverLabel(
        'Click to edit point,\n Drag to move point\n',
        true
      )
      features.push(markerHoverLabel)
    }

    if (this._isRouteWaypointSelected() && !isDragPreview) {
      const selectedPoint = this._formatSelectedWaypointFeature()
      features.push(selectedPoint)
    }

    return features
  }

  /**
   * Handle click event on the map.
   * @param {Object} e
   */
  onClick(e) {
    // Marker and route click have priority.
    if (e.defaultPrevented || this._state.preventMapClick) {
      this._state.preventMapClick = false
      return
    }

    const { mode, waypoints } = routeService
    const { prevClickLocation } = this._state
    const location = Geographic.fromMapbox(e.lngLat)
    const feature = this.getFeatureAtPoint(e.point)

    // Handle clicks of specific features
    if (
      feature?.properties?.type === 'route-waypoint' &&
      routeService.focusedWaypointIndex !== null
    ) {
      const waypointCoords = feature.geometry.coordinates

      this._setSelectedRouteWaypoint(waypointCoords)
      return
    }

    // Handle clicks to commit/add a waypoint in a new location.
    if (
      mode === BUILD_MODE &&
      (!prevClickLocation || !location.equals(prevClickLocation, 6))
    ) {
      this._state.prevClickLocation = location
      routeService.addWaypoint(location)
    }
    // Handle clicks to start building, if there is not an active selected waypoint
    else if (!this._isRouteWaypointSelected()) {
      routeService.mode = BUILD_MODE
      if (waypoints.length === 0) {
        routeService.addWaypoint(location)
      } else if (waypoints.length > 0) {
        routeService.previewWaypoint(location)
      }
    }

    this._removeSelectedWaypoint()
  }

  /**
   * Handle double clicks on the map.
   * @param {Object} e
   */
  onDoubleClick(e) {
    // Prevent map zoom on double click
    e.preventDefault()

    // Stop previewing routes. Will re-enable on map click.
    routeService.mode = EDIT_MODE
  }

  /**
   * Handle mouse down event.
   * @param {Object} e
   */
  onMouseDown(e) {
    // Disable box zoom so that shift+click works.
    if (e.originalEvent.shiftKey) {
      e.preventDefault()
    }

    const feature = this.getFeatureAtPoint(e.point)
    if (!feature) {
      return
    }

    switch (feature?.properties?.type) {
      case 'route-waypoint':
        this._onMarkerMouseDown(e, feature.properties.index)
        break
      case 'route':
      case 'route-hover-point':
        this._onRouteMouseDown(e)
        break
    }
  }

  /**
   * Handle mouse move event.
   * @param {Object} e
   */
  onMouseMove(e) {
    // Call throttled function _mouseMove to prevent performing too many expensive
    // operations on every event. If sufficient delay has elapsed, _handleMouseMove
    // will be called.
    this._mouseMove(e)
  }

  /**
   * Handles the mouse move event
   * @param {MapMouseEvent} e - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleMouseMove(e) {
    if (this._isDraggingMarker) {
      return this._onMarkerDrag(e)
    }
    const { mode, waypoints } = routeService

    if (mode === EDIT_MODE) {
      const features = this.getFeaturesAtPoint(e.point)
      const topFeature = features[0]

      const topFeatureFocused =
        topFeature?.properties.index === routeService.focusedWaypointIndex

      const routeFeature = features.find(
        (feat) => feat?.properties?.type === 'route'
      )

      // If the top feature is a route-waypoint, set it as highlighted
      if (topFeature?.properties?.type === 'route-waypoint') {
        // Set route-waypoint as highlighted, if it is not the focused waypoint
        if (
          !topFeatureFocused ||
          (topFeatureFocused && !this._isRouteWaypointSelected())
        ) {
          setFeaturesHighlighted([topFeature])
          this._state.markerHoverLocation = topFeature.geometry.coordinates
          this.updated()
        }

        this._removeHoverPoint()
      }
      // Else if the set of features at the point contains a route, show the route hover point
      else if (routeFeature) {
        this._handleHoverPoint(e)
        clearFeaturesHighlighted()
        this._removeHoverMarker()
      }
      // Otherwise remove all route hover/highlighting
      else {
        this._removeHoverPoint()
        this._removeHoverMarker()
        clearFeaturesHighlighted()
      }
    } else if (waypoints.length) {
      // Reset the previous click location.
      this._state.prevClickLocation = null

      // Preview the route segment that follows the mouse location.
      const location = Geographic.fromMapbox(e.lngLat)
      routeService.previewWaypoint(location)
    }
  }

  /**
   * Handle mouse up event.
   * @param {Object} e
   */
  onMouseUp(e) {
    if (this._isDraggingMarker) {
      return this._onMarkerRelease(e)
    }
    this._state.draggedMarkerIndex = null
  }

  /**
   * @inheritdoc
   */
  onMouseOut() {
    // call cancel on the throttled function to make sure it doesn't fire _after_ the clear
    this._mouseMove.cancel()
    routeService.clearPreview()
  }

  /**
   * Returns true whan a waypoint marker is being dragged.
   */
  get _isDraggingMarker() {
    return this._state.draggedMarkerIndex !== null
  }

  /**
   * Handle mouse down event on a waypoint marker.
   * @param {Object} e
   * @param {Number} markerIndex - Index of the marker
   * @returns
   */
  _onMarkerMouseDown(e, markerIndex) {
    // Forbid editing waypoints while previewing. This ensures that users can
    // drop multiple waypoint atop one another, if desired.
    if (routeService.mode === BUILD_MODE) {
      return
    }

    // Prevent default mousedown behavior
    e.preventDefault()

    routeService.focusedWaypointIndex = markerIndex

    // Delete if using key modifier.
    if (isDeleteModifierPressed(e.originalEvent)) {
      this._state.preventMapClick = true
      routeService.removeWaypoint(markerIndex)
    } else {
      // update snapping for this waypoint
      routeService.syncShouldSnap(markerIndex)

      // Store state needed to update marker on release.
      this._state.draggedMarkerIndex = markerIndex

      // If mouse is still pressed after a certain time, but the user hasn't started dragging, trigger the selected waypoint state without the popup since this is still a dragged state
      setTimeout(() => {
        if (
          this._state.draggedMarkerIndex === markerIndex &&
          !this._state.didDragMarker
        ) {
          const feature = this.getFeatureAtPoint(e.point)
          this._setSelectedRouteWaypoint(feature.geometry.coordinates, false)
          this.updated()
        }
      }, 150)
    }
  }

  /**
   * Handle "drags" after mouse down on a waypoint marker.
   * @param {Object} e
   */
  _onMarkerDrag(e) {
    // Map clicks are not invoked if the marker is dragged, so cancel the prevent.
    this._state.preventMapClick = false

    // Track that a marker was dragged so we know to update the route on release.
    this._state.didDragMarker = true

    // Set current drag location
    this._state.dragLocation = [e.lngLat.lng, e.lngLat.lat]

    // Calculate the undragged route distance
    if (this._state.undraggedRouteDistance === null) {
      this._state.undraggedRouteDistance = this._getUndraggedRouteDistance()
    }

    // Remove the waypoint popup while dragging, and update display to reflect drag state
    removeRouteWaypointPopup(false)
    this._removeHoverMarker()
    this.updated && this.updated()

    // Preview the effect of drags.
    const location = Geographic.fromMapbox(e.lngLat)
    routeService.updateWaypoint(this._state.draggedMarkerIndex, location, {
      shouldPreview: true,
    })
  }

  /**
   * Handle mouse up after dragging a waypoint marker.
   * @param {Object} e
   */
  _onMarkerRelease(e) {
    // Commit the updated waypoint if it was dragged.
    if (this._state.didDragMarker) {
      const location = Geographic.fromMapbox(e.lngLat)
      routeService.updateWaypoint(this._state.draggedMarkerIndex, location)
      this._removeSelectedWaypoint()

      // Restore the focused route markup opacity
      this.updated && this.updated()
      this._setRouteOpacity(1)
    }

    routeService.restoreShouldSnap()
    this._state.draggedMarkerIndex = null
    this._state.undraggedRouteDistance = null

    // Timeout to allow different route-preview styling for drag vs draw actions
    setTimeout(() => {
      this._state.didDragMarker = false
    }, 100)
  }

  /**
   * Handle mouse down over the route.
   * @param {Object} e
   * @returns
   */
  _onRouteMouseDown(e) {
    if (routeService.mode === BUILD_MODE) {
      return
    }

    // Prevent default and custom click handling.
    e.preventDefault()
    this._state.preventMapClick = true

    const location = Geographic.fromMapbox(e.lngLat)
    const newMarkerIndex = routeService.insertWaypoint(location)
    this._removeHoverPoint()

    this._removeSelectedWaypoint()
    this._onMarkerMouseDown(e, newMarkerIndex)
  }

  /**
   * Handler for when the route waypoints are updated
   * @param {array} waypoints - new waypoint locations
   */
  _handleWaypointsUpdated(waypoints) {
    // If there is a focused waypoint, update the location of it
    if (routeService.focusedWaypointIndex !== null) {
      this._setSelectedRouteWaypoint(
        waypoints[routeService.focusedWaypointIndex].toDegreesTuple(),
        false
      )
    }
    this.updated()
  }

  /**
   * Handler for when the route geojson is updated
   * @param {array} coordinates - array of [lat, lon] coordinates that make up the route line
   */
  async _handleRouteLineUpdated(coordinates) {
    if (coordinates) {
      this._state.sampledLineCoordinates = coordsOnLine(coordinates)

      const elevation = await requestElevation(
        this._state.sampledLineCoordinates
      )
      if (elevation) {
        this._state.routeElevation = elevation
      }
    }
  }

  /**
   * Gets the line coordinates
   * @return {array} coordinates
   */
  _getCoordinates(id) {
    const routeFeature = this.getFeature(id)
    return routeFeature?.geometry?.coordinates
  }

  _setRouteOpacity(value) {
    markupService.editFocusedMarkup({
      geo_json: { properties: { opacity: value } },
    })
  }

  /**
   * Handles the cursor hover point event.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleHoverPoint(e) {
    if (!this._state.sampledLineCoordinates) {
      return
    }

    const lineCoordinates = lineString(this._state.sampledLineCoordinates)
    const hoverPointCoordinate = point([e.lngLat.lng, e.lngLat.lat])
    const locationOnLine = nearestPointOnLine(
      lineCoordinates,
      hoverPointCoordinate
    )
    this._state.routeHoverLocation = locationOnLine.geometry.coordinates
    this.updated()
  }

  /**
   * Removes the cursor hover point.
   * @private
   */
  _removeHoverPoint() {
    if (this._isHoveringRoute()) {
      this._state.routeHoverLocation = null
      this.updated()
    }
  }

  /**
   * Removes the marker hover label.
   * @private
   */
  _removeHoverMarker() {
    if (this._isHoveringMarker()) {
      this._state.markerHoverLocation = null
      this.updated()
    }
  }

  /**
   * Whether the cursor is currently hovering the route.
   * @return {boolean} True if the cursor is hovering the route, false otherwise.
   * @private
   */
  _isHoveringRoute() {
    return this._state.routeHoverLocation !== null
  }

  /**
   * Whether the cursor is currently hovering a marker.
   * @return {boolean} True if the cursor is hovering a route marker, false otherwise.
   * @private
   */
  _isHoveringMarker() {
    return this._state.markerHoverLocation !== null
  }

  /**
   * Formats a point GeoJSON feature for the cursor hover point.
   * @return {Object} Hover point GeoJSON feature to render on the map.
   * @private
   */
  _formatHoverPoint() {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: this._state.routeHoverLocation,
      },
      properties: {
        type: 'route-hover-point',
        meta: 'route-hover-point',
      },
    }
  }

  /**
   * Formats a point GeoJSON feature for the route hover label.
   * @param {string} labelText - text to display in label above state.  Use \n for line breaks
   * @param {Boolean} markerLabel - whether or not the label is for a marker
   * @return {Object} Hover point GeoJSON feature to render on the map.
   * @private
   */
  _formatHoverLabel(labelText, markerLabel = false) {
    const location =
      this._state.markerHoverLocation || this._state.routeHoverLocation

    const { elevation, elevationUnit, distance, distanceUnit } =
      this._getRouteStatsAtLocation(location)

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: location,
      },
      properties: {
        meta: 'route-hover-point-label',
        is_marker_label: markerLabel,
        tooltip_image: 'tooltip_card',
        label_text: labelText,
        distance_point_number: distance,
        distance_point_unit: distanceUnit,
        elevation_point_number: elevation,
        elevation_point_unit: elevationUnit,
      },
    }
  }

  _formatDragLabel() {
    const { distance, distanceUnit, differenceLabel } =
      this._getRouteDragStats(location)

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: this._state.dragLocation,
      },
      properties: {
        meta: 'route-drag-label',
        tooltip_image: 'tooltip_card',
        distance_point_number: distance,
        distance_point_unit: distanceUnit,
        distance_difference: differenceLabel,
      },
    }
  }

  _getRouteDragStats() {
    const { previewSegment } = routeService

    const previewDistance = previewSegment.distance

    const newRouteDistanceInMeters =
      previewDistance + this._state.undraggedRouteDistance

    const convertedDistance = this._state.isImperial
      ? metersToMiles(newRouteDistanceInMeters)
      : metersToKilometers(newRouteDistanceInMeters)

    const formattedDistance = parseInt(convertedDistance)
    const distanceUnit = this._state.isImperial ? ' mi' : ' km'

    const differenceInMeters =
      newRouteDistanceInMeters - routeService.route.data.distance
    const differenceDescriptor = differenceInMeters > 0 ? 'longer' : 'shorter'
    const convertedDifference = this._state.isImperial
      ? metersToMiles(differenceInMeters)
      : metersToKilometers(differenceInMeters)
    const formattedDifference = Math.abs(convertedDifference).toFixed(0)
    const differenceLabel = `${formattedDifference} ${
      this._state.isImperial ? ' miles' : ' kilometers'
    } ${differenceDescriptor}`

    return {
      distance: formattedDistance,
      distanceUnit,
      differenceLabel,
    }
  }

  /**
   * Gets the elevation and distance of a point along the current route line
   * @param {array} location - [lon, lat] location to get the stats for
   * @return {Object} Distance and elevation stats
   * @private
   */
  _getRouteStatsAtLocation(location) {
    if (this._state.sampledLineCoordinates?.length <= 1) {
      return
    }

    const coordinateOnRoute = nearestPointOnLine(
      lineString(this._state.sampledLineCoordinates),
      point(location),
      { units: 'meters' }
    )
    const coordinateIndex = coordinateOnRoute.properties.index

    // Calculate, convert and format distance value
    const routeDistance = routeService.route.data.distance
    const progressPercent =
      coordinateIndex / (this._state.sampledLineCoordinates.length - 1)
    const currentDistanceInMeters = routeDistance * progressPercent
    const convertedDistance = this._state.isImperial
      ? metersToMiles(currentDistanceInMeters)
      : metersToKilometers(currentDistanceInMeters)
    const formattedDistance = convertedDistance.toFixed(1)

    // Calculate, convert, and format elevation value
    let formattedElevation = 0
    if (this._state.routeElevation) {
      const elevationInMeters = this._state.routeElevation[coordinateIndex]
      const convertedElevation = this._state.isImperial
        ? metersToFeet(elevationInMeters)
        : elevationInMeters
      formattedElevation = commaSeparatedNumber(Math.floor(convertedElevation))
    }

    return {
      elevation: formattedElevation,
      elevationUnit: this._state.isImperial ? ' ft' : ' m',
      distance: formattedDistance,
      distanceUnit: this._state.isImperial ? ' mi' : ' km',
    }
  }

  /**
   * Gets the distance of the portion of a route that is not being dragged, while a user is dragging segment in edit mode
   * @return {Number} Distance in meters of the total undragged route segments
   * @private
   */
  _getUndraggedRouteDistance() {
    const currentRoute = routeService?.route
    const totalWaypoints = currentRoute?.waypoints?.length

    const beforeDraggedSegmentMarkerIndex = this._state.draggedMarkerIndex - 1
    const afterDraggedSegmentMarkerIndex = this._state.draggedMarkerIndex + 1
    let beforeDraggedSegmentDistance = 0
    let afterDraggedSegmentDistance = 0

    // Calculate length of segment before dragged segment
    if (beforeDraggedSegmentMarkerIndex > 0) {
      beforeDraggedSegmentDistance = currentRoute?.findLineSegmentDistance(
        0,
        beforeDraggedSegmentMarkerIndex
      )
    }

    // Calculate length of segment after dragged segment
    if (afterDraggedSegmentMarkerIndex < totalWaypoints - 1) {
      afterDraggedSegmentDistance = currentRoute?.findLineSegmentDistance(
        afterDraggedSegmentMarkerIndex,
        totalWaypoints - 1
      )
    }

    const undraggedRouteDistanceInKilometers =
      beforeDraggedSegmentDistance + afterDraggedSegmentDistance

    return kilometersToMeters(undraggedRouteDistanceInKilometers)
  }

  /**
   * @param {Array} location - [lon, lat] location of selected waypoint
   * @param {Boolean} showPopup - whether or not to showPopup with the selected waypoint
   * @private
   */
  _setSelectedRouteWaypoint(location, showPopup = true) {
    this._state.selectedWaypointLocation = location

    if (showPopup) {
      showRouteWaypointPopup(location)
      this._state.markerHoverLocation = null
    } else {
      removeRouteWaypointPopup(false)
    }

    this.updated()
    clearFeaturesHighlighted()
  }

  /**
   * Whether there is currently a selected route waypoint.
   * @return {boolean} True if there is a selected waypoint location, false otherwise.
   * @private
   */
  _isRouteWaypointSelected() {
    return !!this._state.selectedWaypointLocation
  }

  /**
   * Removes the selected waypoint feature.
   * @private
   */
  _removeSelectedWaypoint() {
    this._state.selectedWaypointLocation = null
    routeService.focusedWaypointIndex = null
    removeRouteWaypointPopup()
    this.updated && this.updated()
  }

  /**
   * Formats a point GeoJSON feature for the selected route waypoint.
   * @return {Object} Selected waypoint GeoJSON feature to render on the map.
   * @private
   */
  _formatSelectedWaypointFeature() {
    // Set the color of selected halo to display.  Default of white, darker when the user is dragging
    const selectedColor = this._isDraggingMarker ? '#333333' : '#ffffff'

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: this._state.selectedWaypointLocation,
      },
      properties: {
        type: 'route-waypoint-selected',
        meta: 'route-waypoint-selected',
        circle_color: selectedColor,
      },
    }
  }
}

/**
 * Returns true when the shortcuts for a delete operation are engaged.
 * @param {Event} domEvent
 * @returns
 */
function isDeleteModifierPressed(domEvent) {
  return domEvent.metaKey || domEvent.altKey
}

export default RouteCreateMode
